import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { setToken } from '../utils/tokenStorage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SetPasswordPage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';

  const [verifying, setVerifying] = useState(true);
  const [verifyError, setVerifyError] = useState(null);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) {
      setVerifying(false);
      setVerifyError('Missing activation token. Please use the link from your approval email.');
      return;
    }
    axios.post(`${API}/auth/verify-set-password-token`, { token })
      .then(res => { setEmail(res.data.email); setName(res.data.name); })
      .catch(err => setVerifyError(err.response?.data?.detail || 'Invalid or expired link.'))
      .finally(() => setVerifying(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/auth/set-password`, { token, password }, { withCredentials: true });
      if (res.data.token) setToken(res.data.token);
      navigate('/payment', { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not set password. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const Wrapper = ({ children }) => (
    <div style={{ minHeight: '100vh', background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <div style={{ maxWidth: '480px', width: '100%', background: 'white', borderRadius: '14px', boxShadow: '0 4px 24px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        <div style={{ background: 'linear-gradient(135deg, #059669 0%, #047857 100%)', padding: '30px 24px', color: 'white', textAlign: 'center' }}>
          <h1 style={{ margin: 0, fontSize: '24px' }}>Activate Your Account</h1>
          <p style={{ margin: '6px 0 0', opacity: 0.9 }}>House Sharing Seniors</p>
        </div>
        <div style={{ padding: '28px 24px' }}>{children}</div>
      </div>
    </div>
  );

  if (verifying) return <Wrapper><p style={{ textAlign: 'center', color: '#6b7280' }}>Checking your activation link…</p></Wrapper>;

  if (verifyError) {
    return (
      <Wrapper>
        <div style={{ background: '#fee2e2', border: '1px solid #fecaca', color: '#991b1b', padding: '14px', borderRadius: '8px', marginBottom: '16px' }}>{verifyError}</div>
        <p style={{ color: '#374151' }}>Contact <a href="mailto:newmembers@housesharingseniors.com.au">newmembers@housesharingseniors.com.au</a> for a new link.</p>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      {name && <p style={{ color: '#374151' }}>Welcome, <strong>{name.split(' ')[0]}</strong>. Choose a password to finish setting up your account.</p>}
      <form onSubmit={submit}>
        <label style={{ display: 'block', fontWeight: 600, fontSize: '13px', color: '#374151', marginBottom: '4px' }}>Email</label>
        <input value={email} disabled style={{ width: '100%', padding: '10px', border: '1px solid #e5e7eb', borderRadius: '6px', background: '#f9fafb', color: '#6b7280', marginBottom: '14px' }} />

        <label style={{ display: 'block', fontWeight: 600, fontSize: '13px', color: '#374151', marginBottom: '4px' }}>New Password</label>
        <input type="password" value={password} onChange={e => setPassword(e.target.value)} required minLength={6}
          placeholder="At least 6 characters"
          data-testid="set-password-input"
          style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '6px', marginBottom: '14px' }} />

        <label style={{ display: 'block', fontWeight: 600, fontSize: '13px', color: '#374151', marginBottom: '4px' }}>Confirm Password</label>
        <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} required minLength={6}
          data-testid="confirm-password-input"
          style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '6px', marginBottom: '16px' }} />

        {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '10px', borderRadius: '6px', marginBottom: '14px', fontSize: '14px' }}>{error}</div>}

        <button type="submit" disabled={submitting} data-testid="activate-account-btn"
          style={{ width: '100%', background: '#059669', color: 'white', padding: '12px', border: 'none', borderRadius: '8px', fontWeight: 600, fontSize: '16px', cursor: submitting ? 'wait' : 'pointer', opacity: submitting ? 0.6 : 1 }}>
          {submitting ? 'Activating…' : 'Activate & Continue to Payment'}
        </button>
      </form>
      <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '18px', textAlign: 'center' }}>Next step after activation: your membership payment.</p>
    </Wrapper>
  );
};

export default SetPasswordPage;
